import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SketchPicker } from 'react-color';
import QRCodeStyling from 'qr-code-styling';
import { 
  FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube, 
  FaTiktok, FaSnapchat, FaWhatsapp, FaTelegram, FaPinterest, FaGithub, FaGlobe,
  FaFileAlt, FaThumbsUp, FaPhone, FaBriefcase, FaEnvelope, FaMapMarkerAlt, FaBuilding,
  FaStar, FaFilePdf
} from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import ServicesShowcase from '../components/ServicesShowcase';
import BrochuresManager from '../components/BrochuresManager';
import frame1 from '../assets/frames/frame-1.svg';
import frame2 from '../assets/frames/frame-2.svg';
import frame3 from '../assets/frames/frame-3.svg';
import frame4 from '../assets/frames/frame-4.svg';
import frame5 from '../assets/frames/frame-5.svg';
import frame6 from '../assets/frames/frame-6.svg';
import './VCardCreate.css';

// Social icon mapping
const getSocialIcon = (platform) => {
  const icons = {
    facebook: FaFacebook,
    instagram: FaInstagram,
    twitter: FaTwitter,
    linkedin: FaLinkedin,
    youtube: FaYoutube,
    tiktok: FaTiktok,
    snapchat: FaSnapchat,
    whatsapp: FaWhatsapp,
    telegram: FaTelegram,
    pinterest: FaPinterest,
    github: FaGithub,
  };
  return icons[platform?.toLowerCase()] || FaGlobe;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Normalize frontend URL (remove trailing slash to prevent double slashes)
const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000').replace(/\/+$/, '');

const VCardCreate = ({ initialFormData, isEditMode = false, vCardId = null, onSave }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // Step 1: Contact Details, Step 2: QR Customization
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'qrcode'
  const [previewTab, setPreviewTab] = useState('about'); // 'about', 'contact', or 'services'
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [tempSessionId] = useState(() => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const qrCodeRef = useRef(null);
  const [availableFrames, setAvailableFrames] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [modifiedFrameUrl, setModifiedFrameUrl] = useState(null);
  
  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any color picker popup or wrapper
      const target = event.target;
      const isClickInsidePicker = target.closest('.color-picker-popup') || target.closest('.color-picker-wrapper');
      
      if (!isClickInsidePicker && showColorPicker) {
        setShowColorPicker(null);
      }
    };

    // Add event listener when a color picker is open
    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);
  
  // Default QR config
  const defaultQRConfig = {
    type: 'canvas',
    shape: 'square',
    width: 300,
    height: 300,
    margin: 0,
    qrOptions: {
      typeNumber: '0',
      mode: 'Byte',
      errorCorrectionLevel: 'Q'
    },
    imageOptions: {
      saveAsBlob: true,
      hideBackgroundDots: true,
      imageSize: 0.4,
      margin: 0
    },
    dotsOptions: {
      type: 'square',
      color: '#000000',
      roundSize: true,
      gradient: {
        type: 'linear',
        rotation: 0,
        colorStops: [
          { offset: 0, color: '#000000' },
          { offset: 1, color: '#000000' }
        ]
      }
    },
    backgroundOptions: {
      round: 0,
      color: '#ffffff'
    },
    image: null,
    cornersSquareOptions: {
      type: 'square',
      color: '#000000'
    },
    cornersDotOptions: {
      type: 'square',
      color: '#000000'
    },
      frame: null, // Frame SVG path
      frameId: null, // Selected frame ID
      frameText: 'Scan me!', // Customizable frame text
      frameTextColor: '#000000', // Frame text color
      frameColor: '#000000', // Frame border/background color
      frameBackgroundColor: '#FFFFFF' // Frame background color
    };

  const [formData, setFormData] = useState(() => {
    // Default values
    const defaults = {
      // Design
      primaryColor: '#6594FF',
      secondaryColor: '#FFFFFF',
      backgroundColor: '#F5F5F5', // Page background color (for browser view)
      
      // Personal Information
      fullName: '',
      profileImage: null,
      profileImageUrl: '',
      
      // Contact Details
      phoneNumber: '',
      alternativePhoneNumbers: [],
      website: '',
      emails: [],
      
      // Company Details
      companyName: '',
      title: '',
      
      // Summary
      summary: '',
      
      // Address
      addressType: 'manual',
      address: {
        street: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
      },
      addressUrl: '',
      
      // Social Media
      socialMedia: [],
      
      // Welcome Screen
      welcomeScreenImage: null,
      welcomeScreenImageUrl: '',
      
      // Services Showcase
      services: [],
      
      // Brochures/Flipbook
      brochures: [],
    };
    
    // Merge with initialFormData if provided
    const baseData = initialFormData ? { ...defaults, ...initialFormData } : defaults;

    // Ensure qrConfig always exists with defaults, merging with any existing qrConfig
    return {
      ...baseData,
      qrConfig: {
        ...defaultQRConfig,
        ...(baseData.qrConfig || {})
      }
    };
  });

  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [selectedSocialPlatform, setSelectedSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedVCard, setSavedVCard] = useState(null);

  // Load available frames
  useEffect(() => {
    const frames = [
      { id: 'frame-1', path: frame1, name: 'Frame 1' },
      { id: 'frame-2', path: frame2, name: 'Frame 2' },
      { id: 'frame-3', path: frame3, name: 'Frame 3' },
      { id: 'frame-4', path: frame4, name: 'Frame 4' },
      { id: 'frame-5', path: frame5, name: 'Frame 5' },
      { id: 'frame-6', path: frame6, name: 'Frame 6' }
    ];
    setAvailableFrames(frames);
  }, []);

  // Common method to get QR positioning for frames
  const getQRFramePosition = (frameId) => {
    // Frame SVG viewBox is 64x84
    const framePositions = {
      'frame-1': {
        // Frame-1: qr-background is a rounded rect at x=6, y=10, width=52, height=44
        top: '11.9%',    // 10/84 = 11.9%
        left: '9.375%',  // 6/64 = 9.375%
        width: '81.25%', // 52/64 = 81.25%
        height: '52.38%', // 44/84 = 52.38%
        padding: '4px'
      },
      'frame-2': {
        // Frame-2: qr-background path M6 10 with rounded corners (radius 4)
        top: '6.5%',     // Slightly above 6/84 = 7.14% to cover border
        left: '8.9%',    // Slightly left of 6/64 = 9.375% to cover border
        width: '82.5%',  // Slightly larger than 52/64 = 81.25% to cover border
        height: '63.1%', // Slightly larger than 52/84 = 61.9% to cover border
        padding: '1px'   // Minimal padding
      },
      'frame-3': {
        // Frame-3: Similar to frame-1, qr-background at x=6, y=10, width=52, height=44
        top: '11.9%',
        left: '9.375%',
        width: '81.25%',
        height: '52.38%',
        padding: '4px'
      },
      'frame-4': {
        // Frame-4: qr-background rect at x=11, y=24, width=42, height=42
        top: '28.57%',   // 24/84 = 28.57%
        left: '17.19%',  // 11/64 = 17.19%
        width: '65.63%', // 42/64 = 65.63%
        height: '50%',   // 42/84 = 50%
        padding: '2px'
      },
      'frame-5': {
        // Frame-5: qr-background at x=6, y=10, width=52, height=44
        top: '11.9%',
        left: '9.375%',
        width: '81.25%',
        height: '52.38%',
        padding: '4px'
      },
      'frame-6': {
        // Frame-6: qr-background rect at x=14.5, y=24, width=35, height=35
        top: '28.57%',   // 24/84 = 28.57%
        left: '22.66%',  // 14.5/64 = 22.66%
        width: '54.69%', // 35/64 = 54.69%
        height: '41.67%', // 35/84 = 41.67%
        padding: '2px'
      }
    };
    return framePositions[frameId] || framePositions['frame-1'];
  };

  // Function to modify frame SVG with custom text and colors
  const getModifiedFrameSVG = async (framePath, frameText, frameTextColor, frameColor, frameBackgroundColor) => {
    try {
      const response = await fetch(framePath);
      let svgText = await response.text();
      
      // Replace text content - match the text element and replace its content (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?>)(.*?)(<\/text>)/g, (match, openTag, oldText, closeTag) => {
        return `${openTag}${frameText || 'Scan me!'}${closeTag}`;
      });
      
      // Update text color - find text element and update its fill attribute (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        return `${beforeFill}${fillAttr}${frameTextColor || '#000000'}${afterFill}`;
      });
      
      // If text doesn't have fill attribute, add it (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          return `${tag} fill="${frameTextColor || '#000000'}"${closing}`;
        }
        return match;
      });
      
      // Update elements with id="frame" (paths or groups) - apply frame color (handles multiline)
      // Handle path elements with id="frame"
      svgText = svgText.replace(/(<(?:path|g)[\s\S]*?id="frame"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip if it's transparent or none (those are borders, handled by stroke)
        if (oldColor !== 'transparent' && oldColor !== 'none') {
          return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
        }
        return match;
      });
      
      // Update elements inside text-container - these should get frame color (handles multiline)
      // Find text-container and update path fills within it
      const textContainerRegex = /(<g[\s\S]*?id="text-container"[\s\S]*?>)([\s\S]*?)(<\/g>)/g;
      svgText = svgText.replace(textContainerRegex, (match, openTag, content, closeTag) => {
        // Update path fills within text-container (button backgrounds) - handles multiline
        const updatedContent = content.replace(/(<path[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (pathMatch, beforeFill, fillAttr, pathColor, afterFill) => {
          // Skip if it's white (text might be on white background)
          if (pathColor.toLowerCase() !== 'white' && pathColor !== '#ffffff' && pathColor !== '#FFFFFF') {
            return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
          }
          return pathMatch;
        });
        // Update path strokes within text-container
        const updatedContent2 = updatedContent.replace(/(<path[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (pathMatch, beforeStroke, strokeAttr, pathColor, afterStroke) => {
          if (pathColor !== 'transparent' && pathColor !== 'none') {
            return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
          }
          return pathMatch;
        });
        return openTag + updatedContent2 + closeTag;
      });
      
      // Update all path fills that are borders/lines (not qr-background, frame-background, or icons) - handles multiline
      svgText = svgText.replace(/(<path[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip qr-background and frame-background (handled separately)
        if (match.includes('qr-background') || match.includes('frame-background')) {
          return match;
        }
        // Skip icon paths (youtube icon should stay white)
        if (match.includes('id="youtube"')) {
          return match;
        }
        // Skip transparent fills
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        
        // Check context to determine if this is a border/line element
        const fullMatch = beforeFill + fillAttr + oldColor + afterFill;
        const matchIndex = svgText.indexOf(fullMatch);
        if (matchIndex !== -1) {
          const beforeMatch = svgText.substring(Math.max(0, matchIndex - 200), matchIndex);
          const pathElement = svgText.substring(matchIndex, Math.min(svgText.length, matchIndex + 200));
          
          // If it's a horizontal line (like frame-1's M1 62h62v2H1z) or other border line
          // Or if it's an arrow path (frame-5) or other decorative element
          // Update to frame color
          if (pathElement.includes('h62') || pathElement.includes('v2') || 
              pathElement.includes('id="arrow"') ||
              (!beforeMatch.includes('text-container') && !beforeMatch.includes('qr-background') && !beforeMatch.includes('frame-background'))) {
            return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
          }
        }
        // Default: don't change
        return match;
      });
      
      // Update all strokes (borders) with frame color - need to match in context (handles multiline)
      // Match stroke within path/rect/g elements, but skip qr-background and frame-background
      svgText = svgText.replace(/(<(?:path|rect|g)[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldColor, afterStroke) => {
        // Don't replace if it's part of qr-background or frame-background (frame-background stroke handled separately)
        if (match.includes('qr-background') || match.includes('frame-background')) {
          return match;
        }
        // Don't replace icon paths
        if (match.includes('id="youtube"')) {
          return match;
        }
        // Don't replace transparent or none strokes
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        // Update all other strokes to frame color
        return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
      });
      
      // Update rect fills for frame elements (not frame-background or qr-background) - handles multiline
      svgText = svgText.replace(/(<rect[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip if already handled (frame-background or qr-background)
        if (match.includes('id="frame-background"') || match.includes('id="qr-background"')) {
          return match; // Already handled above
        }
        // Skip transparent fills
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        // Update other rect fills to frame color
        return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
      });
      
      // Update qr-background fill color - make it transparent so QR code shows through (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Make qr-background transparent so QR code shows through without grey background
        return `${beforeFill}${fillAttr}transparent${afterFill}`;
      });
      
      // If qr-background doesn't have fill attribute, add it as transparent (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          return `${tag} fill="transparent"${closing}`;
        }
        return match;
      });
      
      // Remove stroke from qr-background to eliminate any grey border (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldColor, afterStroke) => {
        return `${beforeStroke}${strokeAttr}none${afterStroke}`;
      });
      
      // If qr-background doesn't have stroke attribute, ensure it won't show a border (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('stroke=')) {
          return `${tag} stroke="none"${closing}`;
        }
        return match;
      });
      
      // Update frame-background element (can be rect or path) - this is the main background
      // Find the element and update its fill attribute directly
      
      // Method 1: If fill attribute exists, replace it (handles multiline)
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldValue, afterFill) => {
        return `${beforeFill}${fillAttr}${frameBackgroundColor || '#FFFFFF'}${afterFill}`;
      });
      
      // Method 2: If fill attribute doesn't exist, add it before the closing >
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          // Add fill before the closing >
          return `${tag} fill="${frameBackgroundColor || '#FFFFFF'}"${closing}`;
        }
        return match;
      });
      
      // Update stroke color on frame-background to frame color (for borders)
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldValue, afterStroke) => {
        if (oldValue !== 'transparent' && oldValue !== 'none') {
          return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
        }
        return match;
      });
      
      // Update elements with class="frame-color" - apply frame background color (handles multiline)
      // Method 1: If fill attribute exists, replace it
      svgText = svgText.replace(/(<(?:rect|path|circle|ellipse|polygon|polyline|line)[\s\S]*?class="frame-color"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldValue, afterFill) => {
        return `${beforeFill}${fillAttr}${frameBackgroundColor || '#FFFFFF'}${afterFill}`;
      });
      
      // Method 2: If fill attribute doesn't exist, add it before the closing >
      svgText = svgText.replace(/(<(?:rect|path|circle|ellipse|polygon|polyline|line)[\s\S]*?class="frame-color"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          // Add fill before the closing >
          return `${tag} fill="${frameBackgroundColor || '#FFFFFF'}"${closing}`;
        }
        return match;
      });
      
      // Convert to data URL
      const blob = new Blob([svgText], { type: 'image/svg+xml' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error modifying frame SVG:', error);
      return framePath; // Return original if modification fails
    }
  };

  // Auto-switch to QR code view when entering step 2
  useEffect(() => {
    if (currentStep === 2) {
      setViewMode('qrcode');
    }
  }, [currentStep]);

  // Update savedVCard when initialFormData changes (for edit mode)
  useEffect(() => {
    if (isEditMode && initialFormData?.uniqueId && !savedVCard) {
      // Set savedVCard with uniqueId so QR code can use it
      setSavedVCard({ uniqueId: initialFormData.uniqueId });
    }
  }, [isEditMode, initialFormData?.uniqueId, savedVCard]);

  // Initialize frame when loading from initialFormData (edit mode)
  useEffect(() => {
    if (initialFormData?.qrConfig?.frameId && availableFrames.length > 0) {
      const frame = availableFrames.find(f => f.id === initialFormData.qrConfig.frameId);
      if (frame && !selectedFrame) {
        setSelectedFrame(frame);
        // If frame already has a modified URL (from saved data), use it
        // Otherwise, generate it with saved customization values
        if (initialFormData.qrConfig.frame && initialFormData.qrConfig.frame.startsWith('blob:')) {
          setModifiedFrameUrl(initialFormData.qrConfig.frame);
        } else if (frame) {
          const frameText = initialFormData.qrConfig.frameText || 'Scan me!';
          const frameTextColor = initialFormData.qrConfig.frameTextColor || '#000000';
          const frameColor = initialFormData.qrConfig.frameColor || '#000000';
          const frameBackgroundColor = initialFormData.qrConfig.frameBackgroundColor || '#FFFFFF';
          
          getModifiedFrameSVG(frame.path, frameText, frameTextColor, frameColor, frameBackgroundColor).then(modifiedUrl => {
            setModifiedFrameUrl(modifiedUrl);
            setFormData(prev => ({
              ...prev,
              qrConfig: {
                ...prev.qrConfig,
                frame: modifiedUrl || frame.path
              }
            }));
          });
        }
      }
    }
  }, [initialFormData?.qrConfig?.frameId, initialFormData?.qrConfig?.frame, initialFormData?.qrConfig?.frameText, initialFormData?.qrConfig?.frameTextColor, initialFormData?.qrConfig?.frameColor, initialFormData?.qrConfig?.frameBackgroundColor, availableFrames, selectedFrame]);

  // Get QR Code URL
  const getQRCodeUrl = useCallback(() => {
    // In edit mode, always try to use the actual vCard URL
    if (isEditMode) {
      // Try to get uniqueId from initialFormData first (loaded from API)
      const uniqueId = initialFormData?.uniqueId || savedVCard?.uniqueId;
      if (uniqueId) {
        const url = `${FRONTEND_URL}/vcard/${uniqueId}`;
        console.log('QR Code URL (edit mode):', url);
        return url;
      }
      // If we have vCardId but no uniqueId yet, return a placeholder
      if (vCardId) {
        console.warn('No uniqueId found in edit mode, using placeholder');
        return `${FRONTEND_URL}/vcard/loading`;
      }
    }
    // If vCard is saved, use its uniqueId
    if (savedVCard?.uniqueId) {
      const url = `${FRONTEND_URL}/vcard/${savedVCard.uniqueId}`;
      console.log('QR Code URL (saved):', url);
      return url;
    }
    // For new creation, only show URL if we have a name (preview mode)
    if (formData.fullName) {
      return `${FRONTEND_URL}/vcard/preview`;
    }
    return 'https://example.com';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialFormData?.uniqueId, savedVCard, vCardId, formData.fullName]);

  // Initialize and Update QR Code Styling
  useEffect(() => {
    if (qrCodeRef.current && viewMode === 'qrcode') {
      const qrCodeUrl = getQRCodeUrl();
      
      // Ensure we have a valid URL
      if (!qrCodeUrl || qrCodeUrl === 'https://example.com' || qrCodeUrl.includes('/loading')) {
        console.warn('Invalid QR code URL, skipping generation:', qrCodeUrl);
        return;
      }
      
      // Clear the container first - this is critical for removing old QR codes
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
      }
      
      // Small delay to ensure DOM is ready after frame switch
      const timeoutId = setTimeout(() => {
        if (qrCodeRef.current) {
          // Double-check container is empty
          qrCodeRef.current.innerHTML = '';
          
          // Adjust QR code size based on whether it's in a frame
          const qrConfig = { ...formData.qrConfig };
          if (qrConfig.frame && qrConfig.frameId) {
            // Scale QR to fit the grey area properly - use consistent sizing
            qrConfig.width = 200;
            qrConfig.height = 200;
          } else {
            // When not in frame, use the configured size or default
            qrConfig.width = qrConfig.width || 300;
            qrConfig.height = qrConfig.height || 300;
          }
          
          // Ensure data is a string and properly formatted
          const qrData = String(qrCodeUrl).trim();
          console.log('Generating QR code with data:', qrData);
          
          // Create new QR code instance
          const qrCode = new QRCodeStyling({
            ...qrConfig,
            data: qrData
          });
          
          qrCode.append(qrCodeRef.current);
        }
      }, 50);
      
      return () => {
        clearTimeout(timeoutId);
        const currentRef = qrCodeRef.current;
        if (currentRef) {
          currentRef.innerHTML = '';
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.qrConfig, formData.qrConfig?.frame, formData.qrConfig?.frameId, formData.fullName, savedVCard, viewMode, isEditMode, vCardId, initialFormData, getQRCodeUrl]);

  const socialPlatforms = [
    { id: 'facebook', name: 'Facebook', icon: 'FaFacebook' },
    { id: 'instagram', name: 'Instagram', icon: 'FaInstagram' },
    { id: 'twitter', name: 'Twitter', icon: 'FaTwitter' },
    { id: 'linkedin', name: 'LinkedIn', icon: 'FaLinkedin' },
    { id: 'youtube', name: 'YouTube', icon: 'FaYoutube' },
    { id: 'tiktok', name: 'TikTok', icon: 'FaTiktok' },
    { id: 'snapchat', name: 'Snapchat', icon: 'FaSnapchat' },
    { id: 'whatsapp', name: 'WhatsApp', icon: 'FaWhatsapp' },
    { id: 'telegram', name: 'Telegram', icon: 'FaTelegram' },
    { id: 'pinterest', name: 'Pinterest', icon: 'FaPinterest' },
    { id: 'github', name: 'GitHub', icon: 'FaGithub' },
  ];

  const colorPresets = [
    { primary: '#6594FF', secondary: '#FFFFFF', name: 'Blue & White' },
    { primary: '#000000', secondary: '#FFFFFF', name: 'Black & White' },
    { primary: '#87CEEB', secondary: '#4169E1', name: 'Light Blue & Blue' },
    { primary: '#9370DB', secondary: '#FFFFFF', name: 'Purple & White' },
    { primary: '#32CD32', secondary: '#000000', name: 'Green & Black' },
    { primary: '#FFD700', secondary: '#FFFFFF', name: 'Yellow & White' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }
    
    // Show preview immediately using FileReader - keep this as fallback
    const reader = new FileReader();
    let base64Preview = null;
    reader.onloadend = () => {
      base64Preview = reader.result;
      if (type === 'profile') {
        setFormData(prev => ({ 
          ...prev, 
          profileImageUrl: base64Preview // Show base64 preview immediately
        }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          welcomeScreenImageUrl: base64Preview // Show base64 preview immediately
        }));
      }
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      const uploadFormData = new FormData();
      const fieldName = type === 'profile' ? 'profileImage' : 'welcomeScreenImage';
      uploadFormData.append(fieldName, file);
      
      const token = user ? localStorage.getItem('token') : null;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      const endpoint = type === 'profile' ? '/profile' : '/welcome';
      const response = await axios.post(`${API_URL}/upload${endpoint}`, uploadFormData, config);
      
      // Get the image URL (could be S3 URL or relative path)
      const imageUrl = response.data.imageUrl;
      
      // If it's already a full URL (S3), use it directly; otherwise construct full URL
      const fullImageUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : (() => {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
            return `${baseUrl}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`;
          })();
      
      // Verify image loads before replacing base64 preview
      const testImg = new Image();
      testImg.onload = () => {
        // Image loaded successfully, update with server URL
        // Store the full URL (S3 or relative) for saving to database
        if (type === 'profile') {
          setFormData(prev => ({ 
            ...prev, 
            profileImage: imageUrl, // Store URL (S3 or relative) for saving to database
            profileImageUrl: fullImageUrl // Store full URL for display
          }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            welcomeScreenImage: imageUrl, // Store URL (S3 or relative) for saving to database
            welcomeScreenImageUrl: fullImageUrl // Store full URL for display
          }));
        }
      };
      testImg.onerror = () => {
        // Server image failed to load, keep base64 preview
        console.warn('Server image failed to load, keeping base64 preview');
        if (type === 'profile') {
          setFormData(prev => ({ 
            ...prev, 
            profileImage: imageUrl, // Still save the URL (S3 or relative)
            profileImageUrl: base64Preview || prev.profileImageUrl // Keep base64 for now
          }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            welcomeScreenImage: imageUrl, // Still save the URL (S3 or relative)
            welcomeScreenImageUrl: base64Preview || prev.welcomeScreenImageUrl // Keep base64 for now
          }));
        }
      };
      testImg.src = fullImageUrl;
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      // Keep base64 preview on error
      if (type === 'profile') {
        setFormData(prev => ({ 
          ...prev, 
          profileImageUrl: base64Preview || prev.profileImageUrl || ''
        }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          welcomeScreenImageUrl: base64Preview || prev.welcomeScreenImageUrl || ''
        }));
      }
    }
  };

  const addEmail = () => {
    if (newEmail && !formData.emails.includes(newEmail)) {
      setFormData(prev => ({ ...prev, emails: [...prev.emails, newEmail] }));
      setNewEmail('');
    }
  };

  const removeEmail = (email) => {
    setFormData(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }));
  };

  const addPhone = () => {
    if (newPhone && !formData.alternativePhoneNumbers.includes(newPhone)) {
      setFormData(prev => ({ ...prev, alternativePhoneNumbers: [...prev.alternativePhoneNumbers, newPhone] }));
      setNewPhone('');
    }
  };

  const removePhone = (phone) => {
    setFormData(prev => ({ ...prev, alternativePhoneNumbers: prev.alternativePhoneNumbers.filter(p => p !== phone) }));
  };

  const addSocialMedia = () => {
    if (selectedSocialPlatform && newSocialUrl) {
      const exists = formData.socialMedia.find(sm => sm.platform === selectedSocialPlatform);
      if (!exists) {
        setFormData(prev => ({
          ...prev,
          socialMedia: [...prev.socialMedia, { platform: selectedSocialPlatform, url: newSocialUrl }]
        }));
        setSelectedSocialPlatform('');
        setNewSocialUrl('');
      }
    }
  };

  const removeSocialMedia = (platform) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: prev.socialMedia.filter(sm => sm.platform !== platform)
    }));
  };

  const handleColorPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary
    }));
  };

  // QR Customization Handlers
  const updateQRConfig = (path, value) => {
    setFormData(prev => {
      const newConfig = { ...defaultQRConfig, ...prev.qrConfig };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return { ...prev, qrConfig: newConfig };
    });
  };

  const handleFrameSelect = async (frame) => {
    // Clear existing QR code first
    if (qrCodeRef.current) {
      qrCodeRef.current.innerHTML = '';
    }
    
    // Clean up old blob URL if exists
    const currentModifiedFrameUrl = modifiedFrameUrl;
    if (currentModifiedFrameUrl && currentModifiedFrameUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentModifiedFrameUrl);
    }
    
    setSelectedFrame(frame);
    
    // Get current frame customization values
    const frameText = formData.qrConfig?.frameText || 'Scan me!';
    const frameTextColor = formData.qrConfig?.frameTextColor || '#000000';
    const frameColor = formData.qrConfig?.frameColor || '#000000';
    const frameBackgroundColor = formData.qrConfig?.frameBackgroundColor || '#FFFFFF';
    
    // Generate modified frame SVG
    try {
      const modifiedUrl = await getModifiedFrameSVG(frame.path, frameText, frameTextColor, frameColor, frameBackgroundColor);
      console.log('Generated frame URL:', modifiedUrl);
      setModifiedFrameUrl(modifiedUrl);
      
      // Update formData with the new frame URL - use modifiedUrl or fallback to original path
      setFormData(prev => ({
        ...prev,
        qrConfig: {
          ...defaultQRConfig,
          ...prev.qrConfig,
          frame: modifiedUrl || frame.path,
          frameId: frame.id,
          frameText: frameText,
          frameTextColor: frameTextColor,
          frameColor: frameColor,
          frameBackgroundColor: frameBackgroundColor
        }
      }));
    } catch (error) {
      console.error('Error generating frame:', error);
      // Fallback to original frame path
      setFormData(prev => ({
        ...prev,
        qrConfig: {
          ...defaultQRConfig,
          ...prev.qrConfig,
          frame: frame.path,
          frameId: frame.id,
          frameText: frameText,
          frameTextColor: frameTextColor,
          frameColor: frameColor,
          frameBackgroundColor: frameBackgroundColor
        }
      }));
    }
  };

  // Update frame when customization changes
  useEffect(() => {
    if (formData.qrConfig?.frameId && selectedFrame) {
      const updateFrame = async () => {
        const frameText = formData.qrConfig?.frameText || 'Scan me!';
        const frameTextColor = formData.qrConfig?.frameTextColor || '#000000';
        const frameColor = formData.qrConfig?.frameColor || '#000000';
        const frameBackgroundColor = formData.qrConfig?.frameBackgroundColor || '#FFFFFF';
        
        // Clean up old URL
        const currentModifiedFrameUrl = modifiedFrameUrl;
        if (currentModifiedFrameUrl && currentModifiedFrameUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentModifiedFrameUrl);
        }
        
        const modifiedUrl = await getModifiedFrameSVG(selectedFrame.path, frameText, frameTextColor, frameColor, frameBackgroundColor);
        setModifiedFrameUrl(modifiedUrl);
        
        // Update formData with the new frame URL
        setFormData(prev => ({
          ...prev,
          qrConfig: {
            ...prev.qrConfig,
            frame: modifiedUrl || selectedFrame.path,
            frameId: prev.qrConfig?.frameId || selectedFrame.id
          }
        }));
      };
      updateFrame();
    } else if (!formData.qrConfig?.frameId && modifiedFrameUrl) {
      // Clean up when frame is removed
      if (modifiedFrameUrl.startsWith('blob:')) {
        URL.revokeObjectURL(modifiedFrameUrl);
      }
      setModifiedFrameUrl(null);
    }
    
    // Cleanup function
    return () => {
      const currentModifiedFrameUrl = modifiedFrameUrl;
      if (currentModifiedFrameUrl && currentModifiedFrameUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentModifiedFrameUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.qrConfig?.frameId, formData.qrConfig?.frameText, formData.qrConfig?.frameTextColor, formData.qrConfig?.frameColor, formData.qrConfig?.frameBackgroundColor, selectedFrame]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      updateQRConfig('image', base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    updateQRConfig('image', null);
    const logoInput = document.getElementById('qrLogo');
    if (logoInput) {
      logoInput.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.fullName) {
      alert('Full name is required');
      return;
    }

    // If edit mode and custom onSave provided, use it
    if (isEditMode && onSave) {
      setSaving(true);
      try {
        await onSave(formData);
      } catch (error) {
        // Error already handled in onSave
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        phoneNumbers: formData.phoneNumber ? [formData.phoneNumber] : [],
        tempSessionId: user ? undefined : tempSessionId,
      };

      // Remove image preview URLs but keep image URLs
      delete submitData.profileImageUrl;
      delete submitData.welcomeScreenImageUrl;

      // Optimize QR config - if logo is base64 and too large, we might want to handle it separately
      // For now, we'll send it but the backend limit is increased to 10mb
      if (submitData.qrConfig?.image) {
        // If image is base64 and very large (>2MB), warn user or optimize
        const base64Size = submitData.qrConfig.image.length * 0.75; // Approximate size in bytes
        if (base64Size > 2 * 1024 * 1024) {
          console.warn('QR logo image is large, consider optimizing');
        }
      }

      const token = user ? localStorage.getItem('token') : null;
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      let response;
      if (isEditMode && vCardId) {
        // Update existing vCard
        response = await axios.put(`${API_URL}/vcards/${vCardId}`, submitData, config);
      } else {
        // Create new vCard
        response = await axios.post(`${API_URL}/vcards`, submitData, config);
      }
      
      setSavedVCard(response.data);
      
      // Update formData with saved vCard data (including brochures if any)
      setFormData(prev => ({
        ...prev,
        ...response.data,
        brochures: response.data.brochures || prev.brochures || [],
      }));

      // If user is not logged in, redirect to login/register
      if (!user) {
        // Store temp vCard ID in sessionStorage for later conversion
        sessionStorage.setItem('tempVCardId', response.data._id);
        sessionStorage.setItem('tempSessionId', tempSessionId);
        navigate('/login', { state: { tempSessionId, vCardId: response.data._id } });
      } else {
        // If editing, stay on edit page. If creating new, go to edit mode so they can add brochures
        if (isEditMode) {
          // Already editing, just update the page
          window.location.reload();
        } else {
          // Navigate to edit mode so user can add brochures
          navigate(`/vcard/edit/${response.data._id}`);
        }
      }
    } catch (error) {
      console.error('Error saving vCard:', error);
      if (error.response?.status === 413 || error.message?.includes('too large')) {
        alert('The data is too large. Please reduce the size of your QR code logo image.');
      } else {
        alert('Failed to save vCard. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const qrCodeUrl = getQRCodeUrl();

  return (
    <div className="vcard-create-page">
      <div className="vcard-create-container">
        {/* Left Panel - Form */}
        <div className="vcard-form-panel">
          <h1>
            {currentStep === 1 ? 'Add content to the vCard QR code' : 'Customize design for the vCard QR code'}
          </h1>

          {/* Step Navigation */}
          <div className="step-navigation">
            <div className="step-indicator">
              <div className={`step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>
                <span className="step-number">{currentStep > 1 ? '✓' : '1'}</span>
                <span className="step-label">Add content</span>
              </div>
              <div className="step-connector"></div>
              <div className={`step ${currentStep === 2 ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Customize QR design</span>
              </div>
            </div>
          </div>

          {/* Step 1: Contact Details */}
          {currentStep === 1 && (
            <>
          {/* Design Section */}
          <div className="form-section">
            <div className="section-header">
              <span className="section-icon">🎨</span>
              <h2>Design and customize</h2>
            </div>
            <p className="section-subtitle">Choose your color scheme</p>
            
            <div className="color-presets">
              {colorPresets.map((preset, idx) => (
                <div
                  key={idx}
                  className={`color-preset ${formData.primaryColor === preset.primary && formData.secondaryColor === preset.secondary ? 'active' : ''}`}
                  onClick={() => handleColorPreset(preset)}
                >
                  <div className="preset-colors">
                    <div style={{ backgroundColor: preset.primary, width: '50%', height: '100%' }}></div>
                    <div style={{ backgroundColor: preset.secondary, width: '50%', height: '100%' }}></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="color-inputs">
              <div className="color-input-group">
                <label>Primary color</label>
                <div className="color-picker-wrapper">
                  <div
                    className="color-swatch"
                    style={{ backgroundColor: formData.primaryColor }}
                    onClick={() => setShowColorPicker(showColorPicker === 'primary' ? null : 'primary')}
                  ></div>
                  <input
                    type="text"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="color-input"
                  />
                  {showColorPicker === 'primary' && (
                    <div className="color-picker-popup">
                      <SketchPicker
                        color={formData.primaryColor}
                        onChangeComplete={(color) => setFormData(prev => ({ ...prev, primaryColor: color.hex }))}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="color-input-group">
                <label>Secondary color</label>
                <div className="color-picker-wrapper">
                  <div
                    className="color-swatch"
                    style={{ backgroundColor: formData.secondaryColor }}
                    onClick={() => setShowColorPicker(showColorPicker === 'secondary' ? null : 'secondary')}
                  ></div>
                  <input
                    type="text"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="color-input"
                  />
                  {showColorPicker === 'secondary' && (
                    <div className="color-picker-popup">
                      <SketchPicker
                        color={formData.secondaryColor}
                        onChangeComplete={(color) => setFormData(prev => ({ ...prev, secondaryColor: color.hex }))}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="color-input-group">
                <label>Background color (Browser view)</label>
                <div className="color-picker-wrapper">
                  <div
                    className="color-swatch"
                    style={{ backgroundColor: formData.backgroundColor || '#F5F5F5' }}
                    onClick={() => setShowColorPicker(showColorPicker === 'background' ? null : 'background')}
                  ></div>
                  <input
                    type="text"
                    value={formData.backgroundColor || '#F5F5F5'}
                    onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                    className="color-input"
                  />
                  {showColorPicker === 'background' && (
                    <div className="color-picker-popup">
                      <SketchPicker
                        color={formData.backgroundColor || '#F5F5F5'}
                        onChangeComplete={(color) => setFormData(prev => ({ ...prev, backgroundColor: color.hex }))}
                      />
                    </div>
                  )}
                </div>
                <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                  Background color for browser view (not visible on mobile)
                </small>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <div className="section-header">
              <span className="section-icon">👤</span>
              <h2>About you</h2>
            </div>
            <p className="section-subtitle">Fill in the information you would like to showcase in your vCard</p>
            
            <div className="collapsible-section">
              <h3>Personal information</h3>
              <div className="form-group">
                <label>Full name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="e.g. Jane Cooper"
                  required
                />
              </div>

              <div className="form-group">
                <label>Image</label>
                <div 
                  className="image-upload-area" 
                  onClick={() => document.getElementById('profileImage').click()}
                  style={{ position: 'relative' }}
                >
                  {formData.profileImageUrl ? (
                    <>
                      <img 
                        src={formData.profileImageUrl} 
                        alt="Profile" 
                        className="uploaded-image"
                        onError={(e) => {
                          // If server image fails, try to show base64 if available
                          console.error('Image failed to load:', formData.profileImageUrl);
                        }}
                      />
                      <div className="image-overlay">
                        <span>Click to change image</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="upload-icon">🏔️</div>
                      <div>
                        <p>Upload image (jpg, png, svg)</p>
                        <p className="upload-hint">Maximum size: 5MB</p>
                        <p className="upload-hint" style={{ fontSize: '0.8rem', color: '#6594FF', marginTop: '5px' }}>
                          📷 Tap to take photo or choose from gallery
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  id="profileImage"
                  accept="image/*"
                  capture="user"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    handleImageUpload(e, 'profile');
                    e.target.value = ''; // Reset input to allow re-uploading same file
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="form-section">
            <div className="section-header">
              <h3>Contact details</h3>
            </div>
            <div className="two-column-form">
              <div className="form-group">
                <label>Phone number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="e.g. +1809999999"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="e.g. pauljones.com"
                />
              </div>
            </div>

            <div className="two-column-form">
              <div className="form-group">
                <label>Alternative phone number</label>
                <div className="input-with-button">
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="e.g. +1809999999"
                  />
                  <button type="button" className="add-button" onClick={addPhone}>+</button>
                </div>
                {formData.alternativePhoneNumbers.map((phone, idx) => (
                  <div key={idx} className="tag-item">
                    <span>{phone}</span>
                    <button type="button" onClick={() => removePhone(phone)}>×</button>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label>Email</label>
                <div className="input-with-button">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="e.g. name@email.com"
                  />
                  <button type="button" className="add-button" onClick={addEmail}>+</button>
                </div>
                {formData.emails.map((email, idx) => (
                  <div key={idx} className="tag-item">
                    <span>{email}</span>
                    <button type="button" onClick={() => removeEmail(email)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div className="form-section">
            <div className="section-header">
              <h3>Company details</h3>
            </div>
            <div className="two-column-form">
              <div className="form-group">
                <label>Company name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="e.g. My company"
                />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Film editor"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="form-section">
            <div className="section-header">
              <h3>Summary</h3>
            </div>
            <div className="form-group">
              <label>Text</label>
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                placeholder="e.g. About my company"
                rows="4"
              />
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <div className="section-header">
              <h3>Address</h3>
            </div>
            <div className="address-type-buttons">
              <button
                type="button"
                className={formData.addressType === 'manual' ? 'active' : ''}
                onClick={() => setFormData(prev => ({ ...prev, addressType: 'manual' }))}
              >
                Manual
              </button>
              <button
                type="button"
                className={formData.addressType === 'url' ? 'active' : ''}
                onClick={() => setFormData(prev => ({ ...prev, addressType: 'url' }))}
              >
                URL
              </button>
            </div>

            {formData.addressType === 'manual' ? (
              <div className="address-form">
                <div className="two-column-form">
                  <div className="form-group">
                    <label>Street</label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      placeholder="e.g. Spring Avenue, 9/18"
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal code</label>
                    <input
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleInputChange}
                      placeholder="e.g. 10005"
                    />
                  </div>
                </div>
                <div className="two-column-form">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      placeholder="e.g. New York City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      placeholder="e.g. New York"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    placeholder="e.g. United States"
                  />
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Url</label>
                <input
                  type="url"
                  name="addressUrl"
                  value={formData.addressUrl}
                  onChange={handleInputChange}
                  placeholder="e.g. www.google.com/maps/search"
                />
              </div>
            )}
          </div>

          {/* Social Media */}
          <div className="form-section">
            <div className="section-header">
              <span className="section-icon">🔗</span>
              <h2>Social networks</h2>
            </div>
            <p className="section-subtitle">Click on the icons below to add social media channels you'd like to display.</p>
            
            <div className="social-platforms-grid">
              {socialPlatforms.map((platform) => {
                const IconComponent = getSocialIcon(platform.id);
                return (
                  <div
                    key={platform.id}
                    className={`social-platform-icon ${formData.socialMedia.find(sm => sm.platform === platform.id) ? 'selected' : ''}`}
                    onClick={() => {
                      if (formData.socialMedia.find(sm => sm.platform === platform.id)) {
                        removeSocialMedia(platform.id);
                      } else {
                        setSelectedSocialPlatform(platform.id);
                      }
                    }}
                    title={platform.name}
                  >
                    <IconComponent className="social-icon" />
                  </div>
                );
              })}
            </div>

            {formData.socialMedia.map((sm) => {
              const platform = socialPlatforms.find(p => p.id === sm.platform);
              const IconComponent = getSocialIcon(sm.platform);
              return (
                <div key={sm.platform} className="social-media-input">
                  <div className="social-media-header">
                    <IconComponent className="social-icon-small" />
                    <span>{platform?.name || sm.platform}</span>
                    <button type="button" className="remove-button" onClick={() => removeSocialMedia(sm.platform)}>🗑️</button>
                  </div>
                  <input
                    type="url"
                    value={sm.url}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        socialMedia: prev.socialMedia.map(item =>
                          item.platform === sm.platform ? { ...item, url: e.target.value } : item
                        )
                      }));
                    }}
                    placeholder="e.g. social-media.com"
                  />
                </div>
              );
            })}

            {selectedSocialPlatform && !formData.socialMedia.find(sm => sm.platform === selectedSocialPlatform) && (
              <div className="social-media-input">
                <div className="social-media-header">
                  {(() => {
                    const platform = socialPlatforms.find(p => p.id === selectedSocialPlatform);
                    const IconComponent = platform ? getSocialIcon(platform.icon) : FaGlobe;
                    return <IconComponent className="social-icon-small" />;
                  })()}
                  <span>{socialPlatforms.find(p => p.id === selectedSocialPlatform)?.name}</span>
                </div>
                <div className="input-with-button">
                  <input
                    type={selectedSocialPlatform === 'whatsapp' ? 'tel' : 'url'}
                    value={newSocialUrl}
                    onChange={(e) => setNewSocialUrl(e.target.value)}
                    placeholder={
                      selectedSocialPlatform === 'whatsapp' 
                        ? 'e.g. +1234567890 (include country code)' 
                        : 'e.g. social-media.com'
                    }
                  />
                  {selectedSocialPlatform === 'whatsapp' && (
                    <small style={{ 
                      display: 'block', 
                      marginTop: '0.25rem', 
                      color: '#666', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic'
                    }}>
                      💡 Include country code (e.g., +1 for USA, +44 for UK)
                    </small>
                  )}
                  <button type="button" className="add-button" onClick={addSocialMedia}>Add</button>
                </div>
              </div>
            )}
          </div>

          {/* Welcome Screen */}
          <div className="form-section">
            <div className="section-header">
              <span className="section-icon">🖼️</span>
              <h2>Welcome screen</h2>
            </div>
            <p className="section-subtitle">Display a custom logo while your page is loading</p>
            
            <div className="form-group">
              <label>Image</label>
              <div 
                className="image-upload-area" 
                onClick={() => document.getElementById('welcomeScreenImage').click()}
                style={{ position: 'relative' }}
              >
                {formData.welcomeScreenImageUrl ? (
                  <>
                    <img 
                      src={formData.welcomeScreenImageUrl} 
                      alt="Welcome Screen" 
                      className="uploaded-image"
                      onError={(e) => {
                        console.error('Welcome image failed to load:', formData.welcomeScreenImageUrl);
                      }}
                    />
                    <div className="image-overlay">
                      <span>Click to change image</span>
                    </div>
                  </>
                ) : (
                    <>
                      <div className="upload-icon">🏔️</div>
                      <div>
                        <p>Upload your welcome screen (jpg, png, svg)</p>
                        <p className="upload-hint">Maximum size: 5 MB</p>
                        <p className="upload-hint" style={{ fontSize: '0.8rem', color: '#6594FF', marginTop: '5px' }}>
                          📷 Tap to take photo or choose from gallery
                        </p>
                      </div>
                    </>
                )}
              </div>
              <input
                type="file"
                id="welcomeScreenImage"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                  handleImageUpload(e, 'welcome');
                  e.target.value = ''; // Reset input to allow re-uploading same file
                }}
              />
            </div>
          </div>

          {/* Services Showcase */}
          <div className="form-section">
            <ServicesShowcase
              services={formData.services || []}
              onChange={(services) => setFormData(prev => ({ ...prev, services }))}
              maxServices={user?.subscription?.features?.maxServices || 2}
              isPremium={user?.subscription?.isPremium || false}
            />
          </div>

          {/* Brochures/Flipbook */}
          <div className="form-section">
            <BrochuresManager
              vcardId={vCardId || savedVCard?._id}
              brochures={formData.brochures || []}
              onChange={(brochures) => {
                setFormData(prev => ({ ...prev, brochures }));
                // Also update savedVCard if it exists
                if (savedVCard) {
                  setSavedVCard(prev => ({ ...prev, brochures }));
                }
              }}
              isPremium={user?.subscription?.isPremium || false}
              onVCardSaveNeeded={async () => {
                // Auto-save the vCard if it hasn't been saved yet
                if (!savedVCard?._id && !vCardId) {
                  // Call handleSave to save the vCard first
                  const submitData = {
                    ...formData,
                    phoneNumbers: formData.phoneNumber ? [formData.phoneNumber] : [],
                    tempSessionId: user ? undefined : tempSessionId,
                  };
                  
                  // Remove image preview URLs but keep image URLs
                  delete submitData.profileImageUrl;
                  delete submitData.welcomeScreenImageUrl;

                  const token = user ? localStorage.getItem('token') : null;
                  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

                  const response = await axios.post(`${API_URL}/vcards`, submitData, config);
                  setSavedVCard(response.data);
                  
                  // Update formData with saved vCard data
                  setFormData(prev => ({
                    ...prev,
                    ...response.data,
                    brochures: response.data.brochures || prev.brochures || [],
                  }));
                  
                  return response.data;
                }
                return savedVCard;
              }}
            />
          </div>

          {/* Step 1 Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={() => {
                if (!formData.fullName) {
                  alert('Full name is required');
                  return;
                }
                setCurrentStep(2);
              }}
              disabled={!formData.fullName}
            >
              Next: Customize QR design →
            </button>
          </div>
          </>
          )}

          {/* Step 2: QR Customization */}
          {currentStep === 2 && (
            <>
              {/* QR Frame Section */}
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon">🖼️</span>
                  <h2>QR code frame</h2>
                </div>
                <p className="section-subtitle">Frames improve your QR code visibility, leading to more scans.</p>
                
                <div className="frames-grid">
                  <div 
                    className={`frame-option ${!formData.qrConfig?.frameId ? 'selected' : ''}`}
                    onClick={() => {
                      // Clear existing QR code first
                      if (qrCodeRef.current) {
                        qrCodeRef.current.innerHTML = '';
                      }
                      
                      // Clean up blob URL if exists
                      const currentModifiedFrameUrl = modifiedFrameUrl;
                      if (currentModifiedFrameUrl && currentModifiedFrameUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(currentModifiedFrameUrl);
                      }
                      
                      setSelectedFrame(null);
                      setModifiedFrameUrl(null);
                      
                      // Clear frame completely
                      setFormData(prev => ({
                        ...prev,
                        qrConfig: { 
                          ...defaultQRConfig, 
                          ...prev.qrConfig, 
                          frame: null, 
                          frameId: null,
                          frameText: prev.qrConfig?.frameText || 'Scan me!',
                          frameTextColor: prev.qrConfig?.frameTextColor || '#000000',
                          frameColor: prev.qrConfig?.frameColor || '#000000',
                          frameBackgroundColor: prev.qrConfig?.frameBackgroundColor || '#FFFFFF'
                        }
                      }));
                    }}
                  >
                    <div className="frame-preview no-frame">No Frame</div>
                  </div>
                  {availableFrames.map((frame) => (
                    <div
                      key={frame.id}
                      className={`frame-option ${formData.qrConfig?.frameId === frame.id ? 'selected' : ''}`}
                      onClick={() => handleFrameSelect(frame)}
                    >
                      <img src={frame.path} alt={frame.name} className="frame-preview" />
                    </div>
                  ))}
                </div>

                {/* Frame Customization Options - Only show if frame is selected */}
                {formData.qrConfig?.frameId && (
                  <>
                    <div className="form-group" style={{ marginTop: '20px' }}>
                      <label>Frame text</label>
                      <input
                        type="text"
                        value={formData.qrConfig?.frameText || 'Scan me!'}
                        onChange={(e) => updateQRConfig('frameText', e.target.value)}
                        placeholder="e.g. Scan me!"
                        maxLength={50}
                      />
                    </div>

                    <div className="color-inputs">
                      <div className="color-input-group">
                        <label>Frame text color</label>
                        <div className="color-picker-wrapper">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: formData.qrConfig?.frameTextColor || '#000000' }}
                            onClick={() => setShowColorPicker(showColorPicker === 'frameTextColor' ? null : 'frameTextColor')}
                          ></div>
                          <input
                            type="text"
                            value={formData.qrConfig?.frameTextColor || '#000000'}
                            onChange={(e) => updateQRConfig('frameTextColor', e.target.value)}
                            className="color-input"
                          />
                          {showColorPicker === 'frameTextColor' && (
                            <div className="color-picker-popup">
                              <SketchPicker
                                color={formData.qrConfig?.frameTextColor || '#000000'}
                                onChangeComplete={(color) => updateQRConfig('frameTextColor', color.hex)}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="color-input-group">
                        <label>Frame color</label>
                        <div className="color-picker-wrapper">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: formData.qrConfig?.frameColor || '#000000' }}
                            onClick={() => setShowColorPicker(showColorPicker === 'frameColor' ? null : 'frameColor')}
                          ></div>
                          <input
                            type="text"
                            value={formData.qrConfig?.frameColor || '#000000'}
                            onChange={(e) => updateQRConfig('frameColor', e.target.value)}
                            className="color-input"
                          />
                          {showColorPicker === 'frameColor' && (
                            <div className="color-picker-popup">
                              <SketchPicker
                                color={formData.qrConfig?.frameColor || '#000000'}
                                onChangeComplete={(color) => updateQRConfig('frameColor', color.hex)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="color-input-group" style={{ marginTop: '15px' }}>
                      <label>Frame background color</label>
                      <div className="color-picker-wrapper">
                        <div
                          className="color-swatch"
                          style={{ backgroundColor: formData.qrConfig?.frameBackgroundColor || '#FFFFFF' }}
                          onClick={() => setShowColorPicker(showColorPicker === 'frameBackgroundColor' ? null : 'frameBackgroundColor')}
                        ></div>
                        <input
                          type="text"
                          value={formData.qrConfig?.frameBackgroundColor || '#FFFFFF'}
                          onChange={(e) => updateQRConfig('frameBackgroundColor', e.target.value)}
                          className="color-input"
                        />
                        {showColorPicker === 'frameBackgroundColor' && (
                          <div className="color-picker-popup">
                            <SketchPicker
                              color={formData.qrConfig?.frameBackgroundColor || '#FFFFFF'}
                              onChangeComplete={(color) => updateQRConfig('frameBackgroundColor', color.hex)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* QR Pattern Section */}
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon">🎨</span>
                  <h2>QR code pattern</h2>
                </div>
                <p className="section-subtitle">Select a pattern for your QR code and choose colors.</p>
                
                <div className="form-group">
                  <label>Pattern style</label>
                  <select
                    value={formData.qrConfig?.dotsOptions?.type || 'square'}
                    onChange={(e) => updateQRConfig('dotsOptions.type', e.target.value)}
                    className="pattern-select"
                  >
                    <option value="square">Square</option>
                    <option value="dots">Dots</option>
                    <option value="rounded">Rounded</option>
                    <option value="extra-rounded">Extra Rounded</option>
                    <option value="classy">Classy</option>
                    <option value="classy-rounded">Classy Rounded</option>
                  </select>
                </div>

                <div className="color-inputs">
                  <div className="color-input-group">
                    <label>Dot color</label>
                    <div className="color-picker-wrapper">
                      <div
                        className="color-swatch"
                        style={{ backgroundColor: formData.qrConfig?.dotsOptions?.color || '#000000' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'dotColor' ? null : 'dotColor')}
                      ></div>
                      <input
                        type="text"
                        value={formData.qrConfig?.dotsOptions?.color || '#000000'}
                        onChange={(e) => updateQRConfig('dotsOptions.color', e.target.value)}
                        className="color-input"
                      />
                      {showColorPicker === 'dotColor' && (
                        <div className="color-picker-popup">
                          <SketchPicker
                            color={formData.qrConfig?.dotsOptions?.color || '#000000'}
                            onChangeComplete={(color) => updateQRConfig('dotsOptions.color', color.hex)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="color-input-group">
                    <label>Background color</label>
                    <div className="color-picker-wrapper">
                      <div
                        className="color-swatch"
                        style={{ backgroundColor: formData.qrConfig?.backgroundOptions?.color || '#ffffff' }}
                        onClick={() => setShowColorPicker(showColorPicker === 'bgColor' ? null : 'bgColor')}
                      ></div>
                      <input
                        type="text"
                        value={formData.qrConfig?.backgroundOptions?.color || '#ffffff'}
                        onChange={(e) => updateQRConfig('backgroundOptions.color', e.target.value)}
                        className="color-input"
                      />
                      {showColorPicker === 'bgColor' && (
                        <div className="color-picker-popup">
                          <SketchPicker
                            color={formData.qrConfig?.backgroundOptions?.color || '#ffffff'}
                            onChangeComplete={(color) => updateQRConfig('backgroundOptions.color', color.hex)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Corners Section */}
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon">🔲</span>
                  <h2>QR code corners</h2>
                </div>
                <p className="section-subtitle">Choose your QR code corner style.</p>
                
                <div className="corner-styles">
                  <div className="corner-style-group">
                    <label>Corner frames style</label>
                    <div className="corner-options">
                      {['square', 'extra-rounded', 'dot', 'classy', 'classy-rounded'].map((type) => (
                        <div
                          key={type}
                          className={`corner-option ${formData.qrConfig?.cornersSquareOptions?.type === type ? 'selected' : ''}`}
                          onClick={() => updateQRConfig('cornersSquareOptions.type', type)}
                        >
                          <div className={`corner-preview corner-${type}`}></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="corner-style-group">
                    <label>Corner dots type</label>
                    <div className="corner-options">
                      {['square', 'dot', 'classy', 'classy-rounded'].map((type) => (
                        <div
                          key={type}
                          className={`corner-option ${formData.qrConfig?.cornersDotOptions?.type === type ? 'selected' : ''}`}
                          onClick={() => updateQRConfig('cornersDotOptions.type', type)}
                        >
                          <div className={`corner-preview corner-${type}`}></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="color-inputs">
                    <div className="color-input-group">
                      <label>Corner frames color</label>
                      <div className="color-picker-wrapper">
                        <div
                          className="color-swatch"
                          style={{ backgroundColor: formData.qrConfig?.cornersSquareOptions?.color || '#000000' }}
                          onClick={() => setShowColorPicker(showColorPicker === 'cornerFrameColor' ? null : 'cornerFrameColor')}
                        ></div>
                        <input
                          type="text"
                          value={formData.qrConfig?.cornersSquareOptions?.color || '#000000'}
                          onChange={(e) => updateQRConfig('cornersSquareOptions.color', e.target.value)}
                          className="color-input"
                        />
                        {showColorPicker === 'cornerFrameColor' && (
                          <div className="color-picker-popup">
                            <SketchPicker
                              color={formData.qrConfig?.cornersSquareOptions?.color || '#000000'}
                              onChangeComplete={(color) => updateQRConfig('cornersSquareOptions.color', color.hex)}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="color-input-group">
                      <label>Corner dots color</label>
                      <div className="color-picker-wrapper">
                        <div
                          className="color-swatch"
                          style={{ backgroundColor: formData.qrConfig?.cornersDotOptions?.color || '#000000' }}
                          onClick={() => setShowColorPicker(showColorPicker === 'cornerDotColor' ? null : 'cornerDotColor')}
                        ></div>
                        <input
                          type="text"
                          value={formData.qrConfig?.cornersDotOptions?.color || '#000000'}
                          onChange={(e) => updateQRConfig('cornersDotOptions.color', e.target.value)}
                          className="color-input"
                        />
                        {showColorPicker === 'cornerDotColor' && (
                          <div className="color-picker-popup">
                            <SketchPicker
                              color={formData.qrConfig?.cornersDotOptions?.color || '#000000'}
                              onChangeComplete={(color) => updateQRConfig('cornersDotOptions.color', color.hex)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo Section */}
              <div className="form-section">
                <div className="section-header">
                  <span className="section-icon">🖼️</span>
                  <h2>Add logo</h2>
                </div>
                <p className="section-subtitle">Personalize your QR code by adding a logo or image.</p>
                
                <div className="form-group">
                  <label>Upload your own logo</label>
                  <div 
                    className="image-upload-area" 
                    onClick={() => document.getElementById('qrLogo').click()}
                    style={{ position: 'relative' }}
                  >
                    {formData.qrConfig?.image ? (
                      <>
                        <img 
                          src={formData.qrConfig.image} 
                          alt="QR Logo" 
                          className="uploaded-image"
                          style={{ maxHeight: '100px' }}
                        />
                        <div className="image-overlay">
                          <span>Click to change logo</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="upload-icon">🖼️</div>
                        <div>
                          <p>Upload image (jpg, png, svg)</p>
                          <p className="upload-hint">Maximum size: 5MB</p>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    id="qrLogo"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      handleLogoUpload(e);
                      e.target.value = '';
                    }}
                  />
                  {formData.qrConfig?.image && (
                    <button
                      type="button"
                      className="btn btn-remove-logo"
                      onClick={handleRemoveLogo}
                    >
                      Remove logo
                    </button>
                  )}
                </div>

                {formData.qrConfig?.image && (
                  <div className="form-group">
                    <label>Logo size</label>
                    <input
                      type="range"
                      min="0.1"
                      max="0.5"
                      step="0.05"
                      value={formData.qrConfig?.imageOptions?.imageSize || 0.4}
                      onChange={(e) => updateQRConfig('imageOptions.imageSize', parseFloat(e.target.value))}
                    />
                    <span>{Math.round((formData.qrConfig?.imageOptions?.imageSize || 0.4) * 100)}%</span>
                  </div>
                )}
              </div>

              {/* Step 2 Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentStep(1)}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update vCard' : user ? 'Create →' : 'Continue to Sign Up'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="vcard-preview-panel">
          <div className="preview-header">
            <button
              className={viewMode === 'preview' ? 'active' : ''}
              onClick={() => setViewMode('preview')}
            >
              Preview
            </button>
            <button
              className={viewMode === 'qrcode' ? 'active' : ''}
              onClick={() => setViewMode('qrcode')}
            >
              QR code
            </button>
          </div>

          <div className="phone-preview">
            {viewMode === 'preview' ? (
              <div
                className="vcard-preview"
                style={{
                  background: `linear-gradient(135deg, ${formData.primaryColor} 0%, ${formData.secondaryColor} 100%)`,
                }}
              >
                <div className="preview-header-section">
                  {formData.profileImageUrl ? (
                    <img 
                      src={formData.profileImageUrl.startsWith('http') || formData.profileImageUrl.startsWith('data:') 
                        ? formData.profileImageUrl 
                        : (() => {
                            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                            return `${baseUrl}${formData.profileImageUrl}`;
                          })()} 
                      alt="Profile" 
                      className="preview-profile-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  {!formData.profileImageUrl && (
                    <div className="preview-profile-placeholder">
                      {formData.fullName ? formData.fullName.charAt(0).toUpperCase() : '👤'}
                    </div>
                  )}
                  <h2 className="preview-name">{formData.fullName || 'Your Name'}</h2>
                  <button className="preview-add-contact">+ Add contact</button>
                </div>
                
                {/* Preview Tabs */}
                <div className="preview-tabs">
                  <button 
                    className={`preview-tab ${previewTab === 'about' ? 'active' : ''}`}
                    onClick={() => setPreviewTab('about')}
                  >
                    About
                  </button>
                  <button 
                    className={`preview-tab ${previewTab === 'contact' ? 'active' : ''}`}
                    onClick={() => setPreviewTab('contact')}
                  >
                    Contact
                  </button>
                  <button 
                    className={`preview-tab ${previewTab === 'services' ? 'active' : ''}`}
                    onClick={() => setPreviewTab('services')}
                  >
                    Services & Resources
                  </button>
                </div>

                <div className="preview-content">
                  {/* About Tab Content */}
                  {previewTab === 'about' && (
                    <div className="preview-tab-content">
                      {/* Business Section */}
                      {(formData.companyName || formData.title || (formData.addressType === 'manual' && (formData.address?.street || formData.address?.city))) && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaBriefcase style={{ fontSize: '1.2rem' }} /> Business
                          </div>
                          <div className="preview-section-content">
                            {formData.companyName && (
                              <div className="preview-item">
                                <FaBuilding style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>{formData.companyName}</span>
                              </div>
                            )}
                            {formData.title && (
                              <div className="preview-item">
                                <FaBriefcase style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>{formData.title}</span>
                              </div>
                            )}
                            {formData.addressType === 'manual' && (formData.address?.street || formData.address?.city) && (
                              <div className="preview-item">
                                <FaMapMarkerAlt style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>
                                  {[
                                    formData.address?.street,
                                    formData.address?.city,
                                    formData.address?.state,
                                    formData.address?.postalCode,
                                    formData.address?.country
                                  ].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Summary Section */}
                      {formData.summary && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaFileAlt style={{ fontSize: '1.2rem' }} /> Summary
                          </div>
                          <div className="preview-section-content">
                            <p style={{ margin: 0, padding: '8px 0', textAlign: 'justify' }}>{formData.summary}</p>
                          </div>
                        </div>
                      )}

                      {/* Social Media Section */}
                      {formData.socialMedia?.length > 0 && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaThumbsUp style={{ fontSize: '1.2rem' }} /> Social media ({formData.socialMedia.length})
                          </div>
                          <div className="preview-section-content">
                            {formData.socialMedia.map((sm, idx) => {
                              const platform = socialPlatforms.find(p => p.id === sm.platform);
                              const IconComponent = getSocialIcon(sm.platform);
                              return (
                                <div key={idx} className="preview-item">
                                  {IconComponent && <IconComponent style={{ fontSize: '0.9rem', marginRight: '8px' }} />}
                                  <span>{platform?.name || sm.platform}</span>
                                  {sm.url && <span style={{ marginLeft: '8px', fontSize: '0.85rem', opacity: 0.7 }}>{sm.url}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contact Tab Content */}
                  {previewTab === 'contact' && (
                    <div className="preview-tab-content">
                      {/* Phone Numbers Section */}
                      {(formData.phoneNumber || formData.alternativePhoneNumbers?.length > 0) && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaPhone style={{ fontSize: '1.2rem' }} /> Phone Numbers
                          </div>
                          <div className="preview-section-content">
                            {formData.phoneNumber && (
                              <div className="preview-item">
                                <FaPhone style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>{formData.phoneNumber}</span>
                              </div>
                            )}
                            {formData.alternativePhoneNumbers?.map((phone, idx) => (
                              <div key={idx} className="preview-item">
                                <FaPhone style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>{phone}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Email Addresses Section */}
                      {formData.emails?.length > 0 && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaEnvelope style={{ fontSize: '1.2rem' }} /> Email Addresses
                          </div>
                          <div className="preview-section-content">
                            {formData.emails?.map((email, idx) => (
                              <div key={idx} className="preview-item">
                                <FaEnvelope style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                                <span>{email}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Website Section */}
                      {formData.website && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaGlobe style={{ fontSize: '1.2rem' }} /> Website
                          </div>
                          <div className="preview-section-content">
                            <div className="preview-item">
                              <FaGlobe style={{ fontSize: '0.9rem', marginRight: '8px' }} />
                              <span>{formData.website}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Quick Actions Section */}
                      {(formData.phoneNumber || formData.alternativePhoneNumbers?.length > 0 || formData.emails?.length > 0) && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaPhone style={{ fontSize: '1.2rem' }} /> Quick Actions
                          </div>
                          <div className="preview-section-content">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {(formData.phoneNumber || formData.alternativePhoneNumbers?.length > 0) && (
                                <>
                                  {formData.phoneNumber && (
                                    <div style={{ 
                                      padding: '0.75rem', 
                                      background: '#007bff', 
                                      color: 'white', 
                                      borderRadius: '4px', 
                                      textAlign: 'center',
                                      cursor: 'pointer'
                                    }}>
                                      <FaPhone /> Call {formData.phoneNumber}
                                    </div>
                                  )}
                                  {formData.alternativePhoneNumbers?.map((phone, idx) => (
                                    <div key={idx} style={{ 
                                      padding: '0.75rem', 
                                      background: '#007bff', 
                                      color: 'white', 
                                      borderRadius: '4px', 
                                      textAlign: 'center',
                                      cursor: 'pointer'
                                    }}>
                                      <FaPhone /> Call {phone}
                                    </div>
                                  ))}
                                  {(formData.phoneNumber || formData.alternativePhoneNumbers?.length > 0) && (
                                    <div style={{ 
                                      padding: '0.75rem', 
                                      background: '#25D366', 
                                      color: 'white', 
                                      borderRadius: '4px', 
                                      textAlign: 'center',
                                      cursor: 'pointer'
                                    }}>
                                      <FaWhatsapp /> WhatsApp
                                    </div>
                                  )}
                                </>
                              )}
                              {formData.emails?.length > 0 && (
                                <div style={{ 
                                  padding: '0.75rem', 
                                  background: '#6c757d', 
                                  color: 'white', 
                                  borderRadius: '4px', 
                                  textAlign: 'center',
                                  cursor: 'pointer'
                                }}>
                                  <FaEnvelope /> Email {formData.emails[0]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Services & Resources Tab Content */}
                  {previewTab === 'services' && (
                    <div className="preview-tab-content">
                      {/* Services Showcase Section */}
                      {formData.services?.length > 0 && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaStar style={{ fontSize: '1.2rem' }} /> Product & Services ({formData.services.length})
                          </div>
                          <div className="preview-section-content">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem', marginTop: '0.5rem' }}>
                              {formData.services.map((service, idx) => (
                                <div key={idx} style={{ 
                                  background: '#f8f9fa', 
                                  borderRadius: '6px', 
                                  padding: '0.75rem',
                                  border: service.featured ? '2px solid #ffc107' : '1px solid #e9ecef'
                                }}>
                                  {service.image && (
                                    <img 
                                      src={service.image.startsWith('http') ? service.image : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${service.image}`} 
                                      alt={service.title}
                                      style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  )}
                                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                                    {service.title}
                                    {service.featured && <span style={{ marginLeft: '4px', color: '#ffc107' }}>⭐</span>}
                                  </div>
                                  {service.price && (
                                    <div style={{ fontSize: '0.75rem', color: '#6594FF', fontWeight: '600' }}>{service.price}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Brochures Section */}
                      {formData.brochures?.length > 0 && (
                        <div className="preview-section">
                          <div className="preview-section-header">
                            <FaFilePdf style={{ fontSize: '1.2rem' }} /> Brochures ({formData.brochures.length})
                          </div>
                          <div className="preview-section-content">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem', marginTop: '0.5rem' }}>
                              {formData.brochures.map((brochure, idx) => {
                                const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                                return (
                                  <div key={idx} style={{ 
                                    background: '#f8f9fa', 
                                    borderRadius: '6px', 
                                    padding: '0.5rem',
                                    textAlign: 'center'
                                  }}>
                                    {brochure.thumbnail ? (
                                      <img 
                                        src={brochure.thumbnail.startsWith('http') ? brochure.thumbnail : `${baseUrl}${brochure.thumbnail}`} 
                                        alt={brochure.title}
                                        style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                                        onError={(e) => e.target.style.display = 'none'}
                                      />
                                    ) : (
                                      <div style={{ width: '100%', height: '100px', background: '#e9ecef', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem', fontSize: '2rem' }}>
                                        {brochure.type === 'pdf' ? '📄' : '📊'}
                                      </div>
                                    )}
                                    <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>{brochure.title}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                                      {brochure.type.toUpperCase()} {brochure.pageCount > 0 && `• ${brochure.pageCount} pages`}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="qrcode-preview">
                {qrCodeUrl ? (
                  <div>
                    {formData.qrConfig?.frameId && (formData.qrConfig?.frame || modifiedFrameUrl) ? (
                      <div className="qr-with-frame" key={`frame-${formData.qrConfig.frameId}-${formData.qrConfig.frameText || ''}-${modifiedFrameUrl || formData.qrConfig.frame || ''}`}>
                        <div className="frame-container">
                          <img 
                            src={modifiedFrameUrl || formData.qrConfig.frame} 
                            alt="Frame" 
                            className="frame-overlay"
                            key={`frame-img-${formData.qrConfig.frameId}-${formData.qrConfig.frameText || ''}-${formData.qrConfig.frameTextColor || ''}-${modifiedFrameUrl || formData.qrConfig.frame || ''}`}
                            onLoad={() => {
                              console.log('Frame image loaded successfully:', modifiedFrameUrl || formData.qrConfig.frame);
                            }}
                            onError={(e) => {
                              console.error('Frame image failed to load:', modifiedFrameUrl || formData.qrConfig.frame);
                              // Try fallback to original frame path if available
                              if (selectedFrame && e.target.src !== selectedFrame.path) {
                                e.target.src = selectedFrame.path;
                              }
                            }}
                          />
                          <div 
                            className="qr-in-frame"
                            data-frame={formData.qrConfig.frameId || ''}
                            style={formData.qrConfig.frameId ? (() => {
                              const pos = getQRFramePosition(formData.qrConfig.frameId);
                              return {
                                top: pos.top,
                                left: pos.left,
                                width: pos.width,
                                height: pos.height,
                                padding: pos.padding
                              };
                            })() : {}}
                          >
                            <div ref={qrCodeRef}></div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '300px', alignItems: 'center' }} key="no-frame">
                        <div ref={qrCodeRef}></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p>Fill in your name to generate QR code</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCardCreate;

